import { useMemo } from "react";
import { Box, Chip, Alert, IconButton, Tooltip } from "@mui/material";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import CustomizedDataGrid from "../../customized-data-grid/customized-data-grid";
import { PartialBlob } from "../types";
import { isBlobArchived, isBlobDeleted } from "../utils/blob-status";
import { formatDate, formatFileSize } from "../utils/formatters";

interface FileGridProps {
  blobs: PartialBlob[];
  selectedBlobs: PartialBlob[];
  onSelectionChange: (blobs: PartialBlob[]) => void;
  onDownload: (blob: PartialBlob) => void;
  onDelete: (blob: PartialBlob) => void;
  onRehydrate: (blob: PartialBlob) => void;
}

const FileGrid = ({
  blobs,
  selectedBlobs,
  onSelectionChange,
  onDownload,
  onDelete,
  onRehydrate,
}: FileGridProps) => {
  const rowSelectionModel: GridRowSelectionModel = useMemo(
    () => ({
      type: "include",
      ids: new Set(selectedBlobs.map((blob) => blob.name)),
    }),
    [selectedBlobs],
  );

  const handleSelectionChange = (model: GridRowSelectionModel) => {
    const selected =
      model.type === "include"
        ? blobs.filter((blob) => model.ids.has(blob.name))
        : blobs.filter((blob) => !model.ids.has(blob.name));
    onSelectionChange(selected);
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "File Name",
      flex: 2,
      minWidth: 220,
    },
    {
      field: "contentType",
      headerName: "Content Type",
      flex: 1,
      minWidth: 140,
      valueGetter: (params, row) =>
        row.blobModifiedInformation?.contentType || "N/A",
    },
    {
      field: "size",
      headerName: "Size",
      flex: 0.8,
      minWidth: 100,
      valueGetter: (params, row) => row.blobModifiedInformation?.size ?? null,
      valueFormatter: (params) => formatFileSize(params),
    },
    {
      field: "createdOn",
      headerName: "Created On",
      flex: 1,
      minWidth: 160,
      valueGetter: (params, row) => row.blobModifiedInformation?.createdOn,
      valueFormatter: (params) => formatDate(params),
    },
    {
      field: "lastModifiedOn",
      headerName: "Last Modified",
      flex: 1,
      minWidth: 160,
      valueGetter: (params, row) => row.blobModifiedInformation?.lastModifiedOn,
      valueFormatter: (params) => formatDate(params),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const blob = params.row as PartialBlob;

        if (isBlobDeleted(blob)) {
          return <Chip label="Deleted" color="default" size="small" />;
        }

        const archived = isBlobArchived(blob);
        const archivedOn = blob.blobModifiedInformation?.status?.archivedOn;
        const chip = (
          <Chip
            label={blob.blobModifiedInformation?.status?.statusName ?? "N/A"}
            color={archived ? "default" : "success"}
            size="small"
          />
        );

        return archived && archivedOn ? (
          <Tooltip title={`Archived on ${formatDate(archivedOn)}`}>
            {chip}
          </Tooltip>
        ) : (
          chip
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.2,
      minWidth: 170,
      sortable: false,
      filterable: false,
      headerAlign: "right",
      align: "right",
      renderCell: (params) => {
        const blob = params.row as PartialBlob;
        const deleted = isBlobDeleted(blob);
        const archived = isBlobArchived(blob);

        let downloadTooltip = "Download";
        if (deleted) downloadTooltip = "This file has been deleted";
        else if (archived)
          downloadTooltip = "Rehydrate this file before downloading";

        return (
          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
            <Tooltip title={downloadTooltip}>
              <span>
                <IconButton
                  size="small"
                  aria-label={`Download ${blob.name}`}
                  disabled={deleted || archived}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownload(blob);
                  }}
                  sx={{ color: "primary.main" }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {archived && !deleted && (
              <Tooltip title="Rehydrate from archive">
                <IconButton
                  size="small"
                  aria-label={`Rehydrate ${blob.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRehydrate(blob);
                  }}
                  sx={{ color: "info.main" }}
                >
                  <CloudSyncIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={deleted ? "This file has been deleted" : "Delete"}>
              <span>
                <IconButton
                  size="small"
                  aria-label={`Delete ${blob.name}`}
                  disabled={deleted}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(blob);
                  }}
                  sx={{ color: "error.main" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  if (!blobs || blobs.length === 0) {
    return <Alert severity="info">No files found in this folder.</Alert>;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <CustomizedDataGrid
        data={blobs}
        columns={columns}
        isLoading={false}
        initialSortingField="name"
        rowSelection={false}
        type="file"
        getRowId={(row) => row.name}
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={handleSelectionChange}
        isRowSelectable={(params) => !isBlobDeleted(params.row)}
        height={635}
      />
    </Box>
  );
};

export default FileGrid;
